/**
 * Custom hook for managing featured tracks
 */

import { useState, useCallback } from 'react';
import { useToast } from '@chakra-ui/react';
import {
  featureTrack as featureTrackAPI,
  getTrackFeatureStatus,
  extendFeature as extendFeatureAPI,
  cancelFeature as cancelFeatureAPI,
  getFeaturedTracks,
  getMyFeaturedTracks
} from '../services/featuredTracksService';

export const useFeaturedTracks = () => {
  const [featuredTracks, setFeaturedTracks] = useState([]);
  const [myFeaturedTracks, setMyFeaturedTracks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const toast = useToast();

  // Fetch all active featured tracks (public)
  const fetchFeaturedTracks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getFeaturedTracks();
      setFeaturedTracks(data);
      return data;
    } catch (err) {
      console.error('Error fetching featured tracks:', err);
      setError(err.response?.data?.detail || 'Failed to fetch featured tracks');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch user's featured tracks
  const fetchMyFeaturedTracks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getMyFeaturedTracks();
      setMyFeaturedTracks(data);
      return data;
    } catch (err) {
      console.error('Error fetching my featured tracks:', err);
      setError(err.response?.data?.detail || 'Failed to fetch your featured tracks');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Check feature status for a specific track
  const checkFeatureStatus = useCallback(async (trackId) => {
    try {
      const status = await getTrackFeatureStatus(trackId);
      return status;
    } catch (err) {
      console.error('Error checking feature status:', err);
      return { is_featured: false };
    }
  }, []);

  // Feature a track
  const featureTrack = useCallback(async (trackId, credits, startDate = null) => {
    try {
      setLoading(true);
      setError(null);
      const result = await featureTrackAPI(trackId, credits, startDate);

      toast({
        title: result.is_scheduled ? 'Feature Scheduled' : 'Track Featured',
        description: result.is_scheduled
          ? `Track will be featured starting ${new Date(result.featured_start_date).toLocaleString()}`
          : `Track is now featured for ${credits} hours`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      return result;
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to feature track';
      setError(errorMsg);

      toast({
        title: 'Feature Failed',
        description: errorMsg,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });

      throw err;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // Extend a featured track
  const extendFeature = useCallback(async (trackId, credits) => {
    try {
      setLoading(true);
      setError(null);
      const result = await extendFeatureAPI(trackId, credits);

      toast({
        title: 'Feature Extended',
        description: `Added ${credits} more hours. New expiration: ${new Date(result.new_expiration).toLocaleString()}`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      // Refresh my featured tracks
      await fetchMyFeaturedTracks();

      return result;
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to extend feature';
      setError(errorMsg);

      toast({
        title: 'Extension Failed',
        description: errorMsg,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });

      throw err;
    } finally {
      setLoading(false);
    }
  }, [toast, fetchMyFeaturedTracks]);

  // Cancel a featured track
  const cancelFeature = useCallback(async (trackId) => {
    try {
      setLoading(true);
      setError(null);
      const result = await cancelFeatureAPI(trackId);

      toast({
        title: 'Feature Cancelled',
        description: result.message || 'Feature cancelled successfully',
        status: 'info',
        duration: 5000,
        isClosable: true,
      });

      // Refresh my featured tracks
      await fetchMyFeaturedTracks();

      return result;
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to cancel feature';
      setError(errorMsg);

      toast({
        title: 'Cancellation Failed',
        description: errorMsg,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });

      throw err;
    } finally {
      setLoading(false);
    }
  }, [toast, fetchMyFeaturedTracks]);

  return {
    featuredTracks,
    myFeaturedTracks,
    loading,
    error,
    fetchFeaturedTracks,
    fetchMyFeaturedTracks,
    checkFeatureStatus,
    featureTrack,
    extendFeature,
    cancelFeature
  };
};

export default useFeaturedTracks;
